import type z from 'zod';

import type { NotificationSchema } from '@nesso/common-contracts/notification/models';

import type { WebSocketClient } from './web-socket-client';

type Subscription<T extends NotificationSchema> = {
    schema: T;
    handlers: Set<(payload: z.output<T>['payload']) => void>;
};

export class WebSocketSubscriber {
    private abortController: AbortController = new AbortController();
    private subscriptions: Map<string, Subscription<NotificationSchema>> = new Map();

    connect(client: WebSocketClient): void {
        client.addMessageListener((event) => this.dispatch(event), {
            signal: this.abortController.signal,
        });
    }

    disconnect(): void {
        this.abortController.abort();
        this.abortController = new AbortController();
    }

    subscribe<T extends NotificationSchema>(
        schema: T,
        handler: (payload: z.output<T>['payload']) => void,
    ): void {
        const type = schema.shape.type.value;
        if (!this.subscriptions.has(type)) {
            this.subscriptions.set(type, { schema, handlers: new Set() });
        }
        this.subscriptions.get(type)?.handlers.add(handler);
    }

    unsubscribe<T extends NotificationSchema>(
        schema: T,
        handler: (payload: z.output<T>['payload']) => void,
    ): void {
        const type = schema.shape.type.value;
        const subscription = this.subscriptions.get(type);
        if (!subscription) return;
        subscription.handlers.delete(handler);
        if (subscription.handlers.size === 0) {
            this.subscriptions.delete(type);
        }
    }

    private dispatch(event: MessageEvent): void {
        let raw: z.infer<NotificationSchema>;
        try {
            raw = JSON.parse(event.data) as z.infer<NotificationSchema>;
        } catch {
            console.error('Failed to parse WebSocket message:', event.data);
            return;
        }

        const subscription = this.subscriptions.get(raw.type);
        if (!subscription) return;
        const parsed = subscription.schema.safeParse(raw);
        if (!parsed.success) {
            console.error('Failed to parse WebSocket message payload:', parsed.error);
            return;
        }
        console.debug('Received WebSocket message', {
            type: raw.type,
            payload: parsed.data.payload,
        });
        subscription.handlers.forEach((handler) => handler(parsed.data.payload));
    }
}

export const webSocketSubscriber = new WebSocketSubscriber();
